/**
 * Money is an integer number of cents. Always. Everywhere. (SCOPE §18.1-1)
 *
 * There is no float in this system's money path — not in the database, not on
 * the wire, not in React state. `Cents` is branded so a raw `number` cannot be
 * passed where money is expected without going through a checked constructor.
 */

import type { RatePpm } from './rate';
import { PPM_SCALE } from './rate';
import { divideRounded } from './rounding';

declare const CentsBrand: unique symbol;

/** An integer number of cents. Negative values are legal (refunds, credits). */
export type Cents = number & { readonly [CentsBrand]: true };

export const ZERO_CENTS = 0 as Cents;

/** Wrap a raw integer as {@link Cents}. Throws on non-integer or unsafe input. */
export function cents(value: number): Cents {
  if (!Number.isInteger(value)) {
    throw new TypeError(`cents: money must be a whole number of cents, received ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`cents: value outside safe integer range: ${value}`);
  }
  // Normalise -0 to 0. A fully refunded line negates to -0, which compares
  // unequal to 0 under Object.is and renders as "-$0.00". Neither is something
  // anyone should have to think about downstream.
  return (value === 0 ? 0 : value) as Cents;
}

/** Narrow a bigint intermediate back to {@link Cents}, guarding the safe-integer boundary. */
export function centsFromBigInt(value: bigint): Cents {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new RangeError(`centsFromBigInt: value outside safe integer range: ${value}`);
  }
  return Number(value) as Cents;
}

export function centsToBigInt(value: Cents): bigint {
  return BigInt(value);
}

/**
 * Convert a dollar literal to cents. `dollars(12.5)` -> 1250.
 *
 * For source literals, seeds, and tests **only**. A float that already holds a
 * half-cent value (1.005 is really 1.00499999999999989) has lost the
 * information before this function is called, and no rounding rule recovers it.
 * Operator-typed and imported amounts go through {@link parseMoney} on the
 * original string, where the ambiguity is rejected rather than guessed.
 */
export function dollars(value: number): Cents {
  if (!Number.isFinite(value)) {
    throw new TypeError(`dollars: expected a finite number, received ${value}`);
  }
  return cents(Math.round(value * 100));
}

// --- arithmetic -------------------------------------------------------------

export function addCents(a: Cents, b: Cents): Cents {
  return cents(a + b);
}

export function subtractCents(a: Cents, b: Cents): Cents {
  return cents(a - b);
}

export function sumCents(values: Iterable<Cents>): Cents {
  let total = 0;
  for (const value of values) total += value;
  return cents(total);
}

export function negateCents(value: Cents): Cents {
  return cents(-value);
}

export function absCents(value: Cents): Cents {
  return cents(Math.abs(value));
}

export function minCents(a: Cents, b: Cents): Cents {
  return a <= b ? a : b;
}

export function maxCents(a: Cents, b: Cents): Cents {
  return a >= b ? a : b;
}

/** Floor at zero — used to stop a discount from pushing a line negative. */
export function clampNonNegative(value: Cents): Cents {
  return value < 0 ? ZERO_CENTS : value;
}

/** Exact multiplication by an integer quantity. No rounding occurs. */
export function scaleCents(amount: Cents, quantity: number): Cents {
  if (!Number.isInteger(quantity)) {
    throw new TypeError(`scaleCents: quantity must be an integer, received ${quantity}`);
  }
  return centsFromBigInt(BigInt(amount) * BigInt(quantity));
}

/**
 * `amount × rate`, rounded half away from zero.
 * Used for tax, percentage discounts, and margin math.
 */
export function applyRate(amount: Cents, rate: RatePpm): Cents {
  return centsFromBigInt(divideRounded(BigInt(amount) * BigInt(rate), BigInt(PPM_SCALE)));
}

/**
 * `amount × (1 + rate)`, rounded half away from zero.
 * This is the dual-pricing uplift: cash price in, card price out (SCOPE §8.2).
 */
export function applyUplift(amount: Cents, rate: RatePpm): Cents {
  return centsFromBigInt(
    divideRounded(BigInt(amount) * BigInt(PPM_SCALE + rate), BigInt(PPM_SCALE)),
  );
}

/**
 * `amount × (1 − rate)`, rounded half away from zero — what remains after a
 * percentage discount.
 *
 * Note this rounds the **remainder**, not the discount: 10% off $69.95 leaves
 * $62.96 (from $62.955), and the discount is then derived as $6.99. Rounding
 * the other way would leave $62.95. The remainder is what the customer reads on
 * the line, what gets taxed, and what the sale foots to, so that is the number
 * that gets the rounding treatment.
 */
export function applyDiscountRate(amount: Cents, rate: RatePpm): Cents {
  return centsFromBigInt(
    divideRounded(BigInt(amount) * BigInt(PPM_SCALE - rate), BigInt(PPM_SCALE)),
  );
}

// --- display ----------------------------------------------------------------

export interface FormatCentsOptions {
  /** Currency symbol. Per-tenant config later; USD for now. */
  readonly symbol?: string;
  /** Omit the symbol — for CSV exports and numeric input fields. */
  readonly plain?: boolean;
  /** Render negatives as `($12.34)` instead of `-$12.34`. Accounting reports use this. */
  readonly parenthesizeNegative?: boolean;
  /**
   * To the nearest dollar, halves away from zero, with no cents: `$1,235`. A
   * report shown in whole dollars (decision 218); the figure is exact underneath.
   */
  readonly wholeDollars?: boolean;
}

/**
 * Deterministic money formatting. Hand-rolled rather than `Intl.NumberFormat`
 * because this runs on every keystroke in the cart and must not depend on the
 * register machine's locale — a receipt is not allowed to render `1.234,56`
 * because someone's Windows install is set to de-DE.
 */
export function formatCents(value: Cents, options: FormatCentsOptions = {}): string {
  const {
    symbol = '$',
    plain = false,
    parenthesizeNegative = false,
    wholeDollars = false,
  } = options;

  const magnitude = Math.abs(value);
  // Half a dollar rounds up in size, so a refund rounds the same as its sale.
  const whole = wholeDollars ? Math.floor((magnitude + 50) / 100) : Math.trunc(magnitude / 100);
  const fraction = magnitude % 100;
  // −$0.40 in whole dollars is $0, not −$0.
  const negative = value < 0 && (!wholeDollars || whole > 0);

  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = wholeDollars ? '' : `.${String(fraction).padStart(2, '0')}`;
  const body = `${plain ? '' : symbol}${grouped}${cents}`;

  if (!negative) return body;
  return parenthesizeNegative ? `(${body})` : `-${body}`;
}

// --- parsing ----------------------------------------------------------------

const MONEY_INPUT = /^-?(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/;

/**
 * Parse operator-typed money: `"12.34"`, `"1,234.56"`, `"$12"`, `".5"`, `"-7.00"`.
 *
 * Rejects more than two decimal places rather than silently rounding. Per
 * SCOPE §2.2-7 (error prevention over correction) the register disables the
 * button on invalid input instead of quietly changing what the operator typed.
 */
export function parseMoney(input: string): Cents {
  const parsed = tryParseMoney(input);
  if (parsed === null) {
    throw new TypeError(`parseMoney: cannot parse ${JSON.stringify(input)} as an amount`);
  }
  return parsed;
}

/** Non-throwing {@link parseMoney}. Returns `null` when the input is not a valid amount. */
export function tryParseMoney(input: string): Cents | null {
  const cleaned = input.trim().replace(/[$\s,]/g, '');
  if (cleaned === '' || !MONEY_INPUT.test(cleaned)) return null;

  const negative = cleaned.startsWith('-');
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const [wholePart = '', fractionPart = ''] = unsigned.split('.');

  const whole = wholePart === '' ? 0 : Number(wholePart);
  const fraction = Number(fractionPart.padEnd(2, '0') || '0');

  if (!Number.isSafeInteger(whole)) return null;

  const total = whole * 100 + fraction;
  return cents(negative ? -total : total);
}
